import { type Dictionary } from '@ngrx/entity';
import { type Draft, produce } from 'immer';

import {
  type CanvasElement,
  type CanvasElementId,
  type CanvasNamespaceId,
} from '@blaze-runtime/engine-domain';
import {
  getFormGroupLikeWrapperMainFormId,
  getNewConnectionId,
} from '@blaze-runtime/engine-domain';
import { type ConnectionId } from '@blaze-shared/api-contracts-domain';
import { type RuntimeEventType } from '@blaze-shared/schema-domain';
import {
  type Connection,
  MAIN_INPUT_NAME,
  type RuntimeElement,
  type Workflow,
} from '@blaze-shared/workflow-domain';

import { type EngineState } from '../../+state/engine/engine.model';
import { isFormGroupLike, isWizard } from '../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';

import { getUiRuntimeElement, getWorkflowRuntimeElement } from './runtime';

/** Creates a new connection */
export function createConnection(
  namespaceId: CanvasNamespaceId,
  source: RuntimeElement,
  target: RuntimeElement,
  eventType: RuntimeEventType,
): Connection {
  return {
    id: getNewConnectionId(namespaceId),
    eventType,
    source,
    target,
  } as Connection;
}

/**
 * Looks if provided connection already exists and returns it.
 * If the connection does not exist a new one is created.
 */
export function getOrCreateConnection(
  connections: Connection[],
  namespaceId: CanvasNamespaceId,
  source: RuntimeElement,
  target: RuntimeElement,
  eventType: RuntimeEventType,
): Connection {
  const connection = connections.find(
    (c) =>
      c.source.layerType === source.layerType &&
      c.source.id === source.id &&
      c.target.layerType === target.layerType &&
      c.target.id === target.id &&
      c.eventType === eventType,
  );

  if (connection) {
    return connection;
  }

  return createConnection(namespaceId, source, target, eventType);
}

export function upsertConnections(
  connections: Connection[],
  toUpsert: Connection[] | undefined,
): Connection[] {
  if (!toUpsert) {
    return connections;
  }

  return produce(connections, (draft) => {
    toUpsert.forEach((connection) => {
      const index = draft.findIndex((conn) => conn.id === connection.id);
      if (index === -1) {
        draft.push(connection);
      } else {
        draft[index] = connection;
      }
    });
  });
}

/**
 * Removes the connections to UI elements which are not present
 * @param connections the connection list that is to be filtered
 * @param currentElementIds the list of currently existing element ids (after removal)
 * @returns a new list of connections without the ones which point to elements not present in the provided element list
 */
export function sanitizeConnections(
  connections: Connection[],
  currentElementIds: CanvasElementId[],
): Connection[] {
  // Removes connections that are no longer valid (source or target elements no longer exist)
  const removedUiIds = new Set<string>();
  const inputWorkflowIds = new Set<string>();

  connections.forEach((conn) => {
    const isUiSourceMissing =
      conn.source.layerType === 'Ui' && !currentElementIds.includes(conn.source.id);
    const isUiTargetMissing =
      conn.target.layerType === 'Ui' && !currentElementIds.includes(conn.target.id);

    if (isUiSourceMissing) {
      removedUiIds.add(conn.source.id);
    }
    if (isUiTargetMissing) {
      removedUiIds.add(conn.target.id);
      // Input workflow for removed element
      if (conn.source.layerType === 'Workflow') {
        inputWorkflowIds.add(conn.source.id);
      }
    }
  });

  return connections.filter(
    (conn) =>
      !(
        (conn.source.layerType === 'Ui' && removedUiIds.has(conn.source.id)) || // Connections from the removed element
        (conn.target.layerType === 'Ui' && removedUiIds.has(conn.target.id)) || // Connections to the removed element
        (conn.target.layerType === 'Workflow' && inputWorkflowIds.has(conn.target.id)) // Connections to the input workflow of the element
      ),
  );
}

/**
 * Finds a workflow to which a button element was connected and removes connections from all UI elements which
 * target that workflow. This functions should only be called when a button-like element (element blocking
 * workflow execution) is removed.
 * This is done to make sure that the remaining (now-unblocked) workflow doesn't autoexecute (BL-4888).
 */
export function sanitizeButtonConnectionsMutably(
  draft: Draft<Pick<EngineState, 'connections'>>,
  buttonElementId: string,
): void {
  const connections = draft.connections;
  const connectionIdsToRemove: Set<string> = new Set();

  // Find all connections for which the button element is the source
  const connectionsFromButton = getConnectionsFrom(
    connections,
    getUiRuntimeElement(buttonElementId),
  );
  // Get all workflows connected to the button
  const buttonWorkflowIds = new Set<string>();
  for (const connFromButton of connectionsFromButton) {
    if (connFromButton.target.layerType !== 'Workflow') {
      continue;
    }

    connectionIdsToRemove.add(connFromButton.id);
    buttonWorkflowIds.add(connFromButton.target.id);
  }

  // Find all connections which target the workflow attached to the button.
  for (const wfId of buttonWorkflowIds) {
    const isWfASource = getConnectionsFrom(connections, getWorkflowRuntimeElement(wfId)).length > 0;
    // Remove the connection only if it's not a source for another element.
    if (!isWfASource) {
      const connectionsWithTargetWf = getConnectionsTo(
        connections,
        getWorkflowRuntimeElement(wfId),
      );
      connectionsWithTargetWf.forEach(({ id }) => connectionIdsToRemove.add(id));
    }
  }
  // Remove connections
  draft.connections = draft.connections.filter(({ id }) => !connectionIdsToRemove.has(id));
}

export function getConnectionsFrom(
  connections: Connection[],
  source: RuntimeElement,
): Connection[] {
  return connections.filter(
    (conn) => conn.source.layerType === source.layerType && conn.source.id === source.id,
  );
}

export function getConnectionsTo(connections: Connection[], target: RuntimeElement): Connection[] {
  return connections.filter(
    (conn) => conn.target.layerType === target.layerType && conn.target.id === target.id,
  );
}

export function getSubmitWorkflow(
  containerId: CanvasElementId,
  connections: Connection[],
  workflows: Workflow[],
  elements: Dictionary<CanvasElement> | undefined,
): Workflow | undefined {
  let sourceId = containerId;
  const element = elements && elements[sourceId];
  if (isFormGroupLike(element)) {
    const { mainFormId } = getFormGroupLikeWrapperMainFormId(getWrapperRefs(element));
    if (mainFormId) {
      sourceId = mainFormId;
    }
  }

  const wfConnections = connections.filter(
    (c) =>
      c.source.layerType === 'Ui' && c.source.id === sourceId && c.target.layerType === 'Workflow',
  );
  if (!wfConnections.length) {
    return undefined;
  }
  const potentialWFsIds = wfConnections.map((c) => c.target.id);

  const wfs = workflows.filter(
    (wf) =>
      potentialWFsIds.includes(wf.id) &&
      (isWizard(element) ||
        connections.find(
          (
            c, // WF with button attached
          ) =>
            c.source.layerType === 'Ui' &&
            c.eventType === 'click' &&
            c.target.layerType === 'Workflow' &&
            c.target.id === wf.id,
        )),
  );
  return wfs.length ? wfs[0] : undefined;
}

export function getNamedInputConnections(
  connections: Connection[],
  workflow: Workflow | undefined,
  workflowId: string,
): {
  mainInputConnectionId: string;
  inputConnections: Connection[];
  inputNames: Record<string, string>;
} {
  let mainInputConnectionId = '';
  // Populate input name by connection ID
  const inputConnections = getConnectionsTo(connections, { id: workflowId, layerType: 'Workflow' });
  const inputNames = Object.entries(workflow?.inputs || {}).reduce<Record<ConnectionId, string>>(
    (acc, [name, connectionId]) => {
      // If main input - set main input connection id and do not store this mapping
      if (name === MAIN_INPUT_NAME) {
        mainInputConnectionId = connectionId;
      } else {
        acc[connectionId] = name;
      }
      return acc;
    },
    {},
  );
  // I connections are found but $$input1 connection wasn't, this means that all connections are named input connections.
  // In such a case, default to the first connection as main input.
  if (!mainInputConnectionId && inputConnections?.length) {
    mainInputConnectionId = inputConnections[0].id;
  }
  return { mainInputConnectionId, inputConnections, inputNames };
}

export function findActionConnection(
  connections: Connection[],
  sourceId: string,
  targetId: string,
): Connection | undefined {
  return connections.find(
    (c) => c.source.id === sourceId && c.target.id === targetId && c.eventType === 'action',
  );
}

function hasWorkflowToUiOutput(connections: Connection[], workflowId: string): boolean {
  return connections.some(
    (c) =>
      c.source.layerType === 'Workflow' &&
      c.source.id === workflowId &&
      c.target.layerType === 'Ui',
  );
}

function isWorkflowClickedBy(
  connections: Connection[],
  workflowId: string,
  buttonId: CanvasElementId,
): boolean {
  return connections.some(
    (c) =>
      c.source.layerType === 'Ui' &&
      c.source.id === buttonId &&
      c.eventType === 'click' &&
      c.target.layerType === 'Workflow' &&
      c.target.id === workflowId,
  );
}

/** commitButtonId === undefined → clicked by any button */
function isWorkflowClickedByDifferentButton(
  connections: Connection[],
  workflowId: string,
  commitButtonId: CanvasElementId | undefined,
): boolean {
  return !!connections.find(
    (c) =>
      c.source.layerType === 'Ui' &&
      c.source.id !== commitButtonId &&
      c.eventType === 'click' &&
      c.target.layerType === 'Workflow' &&
      c.target.id === workflowId,
  );
}

export function findSubmitWorkflowConnection(
  connections: Connection[],
  elementId: CanvasElementId,
  commitButtonId: CanvasElementId | undefined,
): Connection | undefined {
  const candidates = connections.filter(
    (c) =>
      c.source.layerType === 'Ui' &&
      c.source.id === elementId &&
      c.target.layerType === 'Workflow' &&
      !hasWorkflowToUiOutput(connections, c.target.id) &&
      !isWorkflowClickedByDifferentButton(connections, c.target.id, commitButtonId),
  );

  const ownedByCommitButton =
    commitButtonId !== undefined
      ? candidates.find((c) => isWorkflowClickedBy(connections, c.target.id, commitButtonId))
      : undefined;

  return ownedByCommitButton ?? candidates[0];
}
